/* src/pages/ServiceDetail.tsx - DETALLE DE SERVICIO */
/* ========================================= */

import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, FileText,  Send } from 'lucide-react';
import { services } from '../data/services';

const ServiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    projectDescription: '',
    budget: ''
  });

  const service = services.find(s => s.id === id);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Servicio no encontrado</h1>
          <Link to="/servicios" className="btn-primary">
            Volver a servicios
          </Link>
        </div>
      </div>
    );
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert(`¡Solicitud de cotización enviada para ${service.name}!`);
    setShowQuoteForm(false);
  };


const handleWhatsAppContact = () => {
  const phoneNumber = '51921259616'; // Tu número de WhatsApp (sin signos + ni espacios)
  const message = `Hola! 👋

Estoy interesado en el servicio de *${service.name}*.

Me gustaría recibir más información sobre:
• Detalles específicos del servicio
• Tiempo de implementación  
• Cotización personalizada

¡Gracias!`;

  // URL para WhatsApp Web y móvil
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
  
  // Abrir en nueva ventana/pestaña
  window.open(whatsappUrl, '_blank');
};

// Alternativa: Si prefieres detectar dispositivo móvil vs desktop
const handleWhatsAppContactAdvanced = () => {
  const phoneNumber = '51921259616';
  const message = `Hola! 👋

Estoy interesado en el servicio de *${service.name}*.

Me gustaría recibir más información sobre:
• Detalles específicos del servicio  
• Tiempo de implementación
• Cotización personalizada

¡Gracias!`;

  // Detectar si es dispositivo móvil
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  let whatsappUrl;
  if (isMobile) {
    // Para dispositivos móviles - abre la app directamente
    whatsappUrl = `whatsapp://send?phone=${phoneNumber}&text=${encodeURIComponent(message)}`;
  } else {
    // Para desktop - abre WhatsApp Web
    whatsappUrl = `https://web.whatsapp.com/send?phone=${phoneNumber}&text=${encodeURIComponent(message)}`;
  }
  
  window.open(whatsappUrl, '_blank');
};


  return (
    <div className="min-h-screen bg-infinity-black">
      {/* Hero Section */}
      <section className="bg-infinity-gradient text-infinity-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <Link
            to="/servicios"
            className="inline-flex items-center text-infinity-white hover:text-infinity-yellow mb-6"
          >
            <ArrowLeft size={20} className="mr-2" />
            Volver a servicios
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="flex flex-col lg:flex-row items-start lg:items-center space-y-6 lg:space-y-0 lg:space-x-6 mb-6">
                <div className="flex items-center space-x-4">
                  <div>
                    <h1 className="text-4xl font-bold">{service.name}</h1>
                    <p className="text-xl text-gray-300 mt-2">{service.description}</p>
                  </div>
                </div>

                {service.image && (
                  <div className="w-full lg:w-80 h-64 rounded-lg overflow-hidden shadow-lg flex-shrink-0">
                    <img
                      src={service.image}
                      alt={`Imagen representativa de ${service.name}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="bg-infinity-dark rounded-lg p-6">
              <h3 className="text-xl font-bold mb-4">¿Necesitas este servicio?</h3>
              <button
                onClick={() => setShowQuoteForm(true)}
                className="w-full btn-primary"
              >
                Solicitar Cotización
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contenido Principal */}
          <div className="lg:col-span-2 space-y-8">
            {/* Descripción Detallada */}
            <div className="bg-infinity-dark rounded-lg p-6">
              <h2 className="text-2xl font-bold text-infinity-yellow mb-4">Descripción del Servicio</h2>
              <p className="text-infinity-white/80 leading-relaxed text-lg">{service.description}</p>
            </div>

            {/* Objetivos */}
            {service.objectives && (
              <div className="bg-infinity-dark rounded-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <FileText size={24} className="text-infinity-yellow" />
                  <h2 className="text-2xl font-bold text-infinity-yellow">Objetivos</h2>
                </div>
                <ul className="space-y-3">
                  {service.objectives.map((objective, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle size={20} className="text-infinity-yellow mt-0.5 flex-shrink-0" />
                      <span className="text-infinity-white/80">{objective}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Alcance del Servicio con Imágenes */}
            {service.scope && (
              <div className="bg-infinity-dark rounded-lg p-6">
                <h2 className="text-2xl font-bold text-infinity-yellow mb-6">Alcance del Servicio</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {service.scope.map((scopeItem, index) => (
                    <div key={index} className="bg-infinity-black rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                      {/* Imagen del Scope */}
                      {scopeItem.image && (
                        <div className="h-48 overflow-hidden">
                          <img
                            src={scopeItem.image}
                            alt={`Imagen representativa de ${scopeItem.name}`}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              // Fallback si la imagen no existe
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        </div>
                      )}

                      {/* Contenido del Scope */}
                      <div className="p-4">
                        <div className="flex items-start space-x-3">
                          <CheckCircle size={20} className="text-infinity-yellow mt-1 flex-shrink-0" />
                          <div>
                            <h3 className="font-semibold text-infinity-white mb-2">{scopeItem.name}</h3>
                            <p className="text-sm text-infinity-white/70">
                              Servicio especializado en {scopeItem.name.toLowerCase()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Características */}
            <div className="bg-infinity-dark rounded-lg p-6">
              <h2 className="text-2xl font-bold text-infinity-yellow mb-4">Lo que Incluye</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {service.features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle size={20} className="text-infinity-yellow mt-0.5 flex-shrink-0" />
                    <span className="text-infinity-white/80">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* CTA Principal */}
            <div className="bg-infinity-dark rounded-lg shadow-lg p-6 sticky top-4">
              <h3 className="text-xl font-bold text-infinity-yellow mb-4">
                Solicita tu Cotización
              </h3>
              <p className="text-infinity-white/80 mb-6">
                Recibe una propuesta personalizada sin compromiso
              </p>
              <button
                onClick={() => setShowQuoteForm(true)}
                className="w-full btn-primary mb-4 bg-infinity-yellow text-infinity-black hover:bg-infinity-purple hover:text-infinity-white"
              >
                Cotizar Ahora
              </button>
              <div className="text-center text-sm text-infinity-white/60">
                ✓ Respuesta en 24 horas<br />
                ✓ Consulta gratuita<br />
                ✓ Sin compromiso
              </div>
            </div>

            {/* Contacto Directo */}
            <div className="bg-infinity-dark rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-infinity-yellow mb-4">
                ¿Prefieres hablar directamente?
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="font-medium text-infinity-yellow">WhatsApp</p>
                  <p className="text-infinity-white/80">+51 921 259 616</p>
                </div>
                <div>
                  <p className="font-medium text-infinity-yellow">Email</p>
                  <p className="text-infinity-white/80">ventas@infinitywits.com</p>
                </div>
                <button
                  onClick={handleWhatsAppContactAdvanced}
                  className="w-full mt-4 btn-secondary bg-infinity-yellow text-infinity-black hover:bg-infinity-purple hover:text-infinity-white flex items-center justify-center space-x-2"
                >
                  <Send size={16} />
                  <span>Contacto Rápido</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Cotización */}
      {showQuoteForm && (
        <div className="fixed inset-0 bg-infinity-black bg-opacity-80 flex items-center justify-center p-4 z-50">
          <div className="bg-infinity-dark rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-infinity-yellow">Solicitar Cotización</h3>
              <button
                onClick={() => setShowQuoteForm(false)}
                className="text-infinity-yellow hover:text-infinity-white"
              >
                ×
              </button>
            </div>

            <p className="text-infinity-white/80 mb-4">
              Servicio: <strong>{service.name}</strong>
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-infinity-yellow mb-1">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-infinity-yellow mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-infinity-yellow mb-1">
                    Teléfono *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-infinity-yellow mb-1">
                    Empresa
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-infinity-yellow mb-1">
                  Presupuesto estimado
                </label>
                <select
                  name="budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white"
                >
                  <option value="">Seleccionar rango</option>
                  <option value="5000-10000">S/ 5,000 - S/ 10,000</option>
                  <option value="10000-25000">S/ 10,000 - S/ 25,000</option>
                  <option value="25000-50000">S/ 25,000 - S/ 50,000</option>
                  <option value="50000+">Más de S/ 50,000</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-infinity-yellow mb-1">
                  Descripción del proyecto *
                </label>
                <textarea
                  name="projectDescription"
                  rows={4}
                  required
                  value={formData.projectDescription}
                  onChange={handleInputChange}
                  placeholder="Describe tu proyecto, ubicación, necesidades específicas..."
                  className="w-full px-4 py-2 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white"
                ></textarea>
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowQuoteForm(false)}
                  className="flex-1 btn-secondary bg-infinity-dark text-infinity-yellow hover:bg-infinity-yellow hover:text-infinity-black"
                >
                  Cancelar
                </button>

                
                <button
                  type="button"
                  onClick={handleWhatsAppContact}
                  className="flex-1 btn-primary flex items-center justify-center space-x-2 bg-infinity-yellow text-infinity-black hover:bg-infinity-purple hover:text-infinity-white"
                >
                  <Send size={16} />
                  <span>Enviar Solicitud</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceDetail;